import { useCallback, useRef } from 'react';
import { useFocusEffect } from 'expo-router';

import { type TelemetryEvent, type TelemetryEventName } from '../../TelemetryEvent';
import { useTelemetry } from '../../TelemetryProvider';

/**
 * A small hook that tracks provided event on screen focus.
 *
 * @see {@link https://reactnavigation.org/docs/use-focus-effect/}
 */
export const useTrackEventOnFocus = <Name extends TelemetryEventName>(
  event: TrackEventFocusEffectPayload<Name>,
  options?: TrackEventFocusEffectOptions,
  onTrack?: () => void,
) => {
  const { trackEvent } = useTelemetry();

  const { skip, once } = options ?? {};

  // ─── Memoized payload ────────────────────────────────────────────────

  const trackEventRef = useRef(trackEvent);
  const eventRef = useRef(event);
  const hasTrackedRef = useRef(false);

  trackEventRef.current = trackEvent;
  eventRef.current = event;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const track = useCallback(() => {
    const trackTelemetryEvent = trackEventRef.current;
    const telemetryEvent = eventRef.current;

    if (skip) return;

    if (once && hasTrackedRef.current) return;

    hasTrackedRef.current = true;

    trackTelemetryEvent(telemetryEvent);
    onTrack?.();
  }, [skip, once, onTrack]);

  // ─────────────────────────────────────────────────────────────────────

  /**
   * Track event on screen focus.
   */
  useFocusEffect(track);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type TrackEventFocusEffectPayload<EventName extends TelemetryEventName> = TelemetryEvent<EventName>;

type TrackEventFocusEffectOptions = {
  skip?: boolean;

  /**
   * Tracks only the first focus of the mounted screen. Use it for events that
   * count visits, so that returning from a pushed screen — which re-focuses
   * the one underneath — does not report a second visit.
   */
  once?: boolean;
};
